import React, { useState, useMemo, useRef, useEffect } from "react";
import { syllabusApi } from "../../config/api";
import { apiClient } from "../../utils/apiClient";
import "./Syllabus.scss";

const COURSES = ["B.A.", "B.Sc.", "B.Com.", "B.B.A.", "B.C.A.", "B.Tech.", "B.Ed.", "LL.B.", "M.A.", "M.Sc.", "M.Com.", "M.B.A.", "M.C.A.", "M.Tech.", "M.Ed.", "LL.M.", "Ph.D."];
const SEMESTERS = ["Semester 1", "Semester 2", "Semester 3", "Semester 4", "Semester 5", "Semester 6", "Semester 7", "Semester 8"];
const ACADEMIC_YEARS = ["2022-23", "2023-24", "2024-25", "2025-26", "2026-27"];
const SUBJECT_TYPES = ["Core", "Elective", "Lab", "Project", "Audit"];
const EXAM_PATTERNS = ["Theory", "Practical", "Theory + Practical", "Internal Only", "Viva"];

const STATUS_META = {
  "Active": { label: "Active", color: "#22c55e", bg: "#f0fdf4", border: "#bbf7d0" },
  "Draft": { label: "Draft", color: "#64748b", bg: "#f8fafc", border: "#cbd5e1" },
  "Under Review": { label: "Under Review", color: "#f59e0b", bg: "#fffbeb", border: "#fde68a" },
  "Archived": { label: "Archived", color: "#ef4444", bg: "#fef2f2", border: "#fecaca" },
};

const COURSE_ICONS = {
  "B.A.": "🎭", "M.A.": "🎭", "B.Sc.": "🔬", "M.Sc.": "🔬", "B.Com.": "💼", "M.Com.": "💼",
  "B.B.A.": "📊", "M.B.A.": "📊", "B.C.A.": "💻", "M.C.A.": "💻", "B.Tech.": "⚙️", "M.Tech.": "⚙️",
  "B.Ed.": "📚", "M.Ed.": "📚", "LL.B.": "⚖️", "LL.M.": "⚖️", "Ph.D.": "🎓",
};

const mkSubject = () => ({ 
  id: Date.now() + Math.random(), 
  Subject_Name: "", 
  Code: "", 
  Units: 5, 
  Credits: 4, 
  Type: "Core", 
  Exam_Pattern: "Theory", 
  Internal_Marks: 40, 
  External_Marks: 60 
});

const defaultForm = {
  _id: null, 
  Syllabus_Code: "", 
  Course: "B.Sc.", 
  Semester: "Semester 1", 
  Academic_Year: "2025-26",
  Subjects: [mkSubject()], 
  Reference_Books: "", 
  Syllabus_PDF_URL: "", 
  Status: "Draft", 
  Approved_By: "", 
  Internal_Remarks: "",
};

const Syllabus = () => {
  const [syllabi, setSyllabi] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalType, setModalType] = useState("");
  const [formData, setFormData] = useState(defaultForm);
  const [selectedItem, setSelectedItem] = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const [confirmAction, setConfirmAction] = useState("");
  const [dropdownOpenId, setDropdownOpenId] = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const [searchText, setSearchText] = useState("");
  const [activeTab, setActiveTab] = useState("info");
  const dropdownRef = useRef(null);

  useEffect(() => { fetchSyllabi(); }, []);

  const fetchSyllabi = async () => {
    try {
      setLoading(true);
      const response = await apiClient(syllabusApi);
      const result = await response.json();
      if (response.ok) { setSyllabi(result.data || []); }
    } catch (error) {
      console.error("Error fetching syllabi:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const onClose = () => setDropdownOpenId(null);
    window.addEventListener("scroll", onClose, true);
    return () => window.removeEventListener("scroll", onClose, true);
  }, []);

  const handleDotsClick = (e, id) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    setDropdownPos({ top: rect.bottom + 8, left: rect.right - 200 });
    setDropdownOpenId(id);
  };

  const openAddModal = () => { setFormData(defaultForm); setActiveTab("info"); setModalType("add"); };
  const openViewModal = (s) => { setSelectedItem(s); setModalType("view"); setDropdownOpenId(null); };
  const openEditModal = (s) => { setFormData({ ...s, Subjects: s.Subjects.map(sub => ({ ...sub })) }); setActiveTab("info"); setModalType("edit"); setDropdownOpenId(null); };
  const closeModal = () => { setModalType(""); setSelectedItem(null); setFormData(defaultForm); };

  const handleSave = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const method = modalType === "add" ? "POST" : "PUT";
      const url = modalType === "add" ? syllabusApi : `${syllabusApi}/${formData._id}`;
      const response = await apiClient(url, { method, body: JSON.stringify(formData) });
      if (response.ok) { fetchSyllabi(); closeModal(); }
    } catch (error) {
      console.error("Error saving syllabus:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (id, newStatus) => {
    try {
      setLoading(true);
      const item = syllabi.find(s => s._id === id);
      await apiClient(`${syllabusApi}/${id}`, { method: "PUT", body: JSON.stringify({ ...item, Status: newStatus }) });
      fetchSyllabi();
      setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error updating status:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      await apiClient(`${syllabusApi}/${id}`, { method: "DELETE" });
      fetchSyllabi();
      setConfirmId(null); setConfirmAction("");
    } catch (error) {
      console.error("Error deleting syllabus:", error);
    } finally {
      setLoading(false);
    }
  };

  const filtered = syllabi.filter(s => 
    (s.Course || "").toLowerCase().includes(searchText.toLowerCase()) || 
    (s.Syllabus_Code || "").toLowerCase().includes(searchText.toLowerCase()) ||
    (s.Subjects || []).some(sub => (sub.name || "").toLowerCase().includes(searchText.toLowerCase()))
  );

  return (
    <div className="syl-page">
      <div className="syl-header">
        <div className="syl-header-left">
          <div className="syl-header-icon">📚</div>
          <div><h1>Syllabus Management</h1><p>Manage course curriculum and credits.</p></div>
        </div>
        <button className="syl-add-btn" onClick={openAddModal}>+ Add Syllabus</button>
      </div>

      <div className="syl-stats">
        <div className="syl-stat-card total"><strong>{syllabi.length}</strong><span>Total</span></div>
        <div className="syl-stat-card active"><strong>{syllabi.filter(s => s.Status === "Active").length}</strong><span>Active</span></div>
        <div className="syl-stat-card review"><strong>{syllabi.filter(s => s.Status === "Under Review").length}</strong><span>Review</span></div>
      </div>

      <div className="syl-toolbar">
        <div className="toolbar-search"><input type="text" placeholder="Search syllabus..." value={searchText} onChange={(e) => setSearchText(e.target.value)} /></div>
      </div>

      {loading && <div className="loading-overlay">Loading...</div>}

      <div className="syl-grid">
        {filtered.map((syl) => {
          const sm = STATUS_META[syl.Status] || STATUS_META.draft;
          return (
            <div key={syl._id} className="syl-card" onClick={() => openViewModal(syl)}>
              <div className="syl-card-top">
                <div className="syl-card-icon-wrap"><span>{COURSE_ICONS[syl.Course] || "📘"}</span></div>
                <div className="syl-card-meta">
                  <span className="syl-card-code">{syl.Syllabus_Code}</span>
                  <span className="status-badge" style={{ color: sm.color, background: sm.bg, border: `1px solid ${sm.border}` }}>{sm.label}</span>
                </div>
                <button className="dots-btn" onClick={(e) => handleDotsClick(e, syl._id)}>⋮</button>
              </div>
              <div className="syl-card-course">{syl.Course}</div>
              <div className="syl-card-sem">{syl.Semester} · {syl.Academic_Year}</div>
              <div className="syl-card-stats">
                <div className="scs-item"><strong>{syl.Subjects?.length || 0}</strong><span>Subjects</span></div>
                <div className="scs-item"><strong>{syl.Subjects?.reduce((a, s) => a + (Number(s.Credits) || 0), 0)}</strong><span>Credits</span></div>
              </div>
              <div className="syl-card-footer"><span>👤 {syl.Approved_By || "—"}</span><span>📅 {syl.createdAt ? new Date(syl.createdAt).toLocaleDateString() : "—"}</span></div>
            </div>
          );
        })}
      </div>

      {dropdownOpenId && (
        <div ref={dropdownRef} className="action-dropdown" style={{ position: "fixed", top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999 }}>
          <button onClick={() => openEditModal(syllabi.find(s => s._id === dropdownOpenId))}>Edit</button>
          <button onClick={() => { setConfirmId(dropdownOpenId); setConfirmAction("publish"); setDropdownOpenId(null); }}>Publish</button>
          <button className="delete" onClick={() => { setConfirmId(dropdownOpenId); setConfirmAction("delete"); setDropdownOpenId(null); }}>Delete</button>
        </div>
      )}

      {(modalType === "add" || modalType === "edit") && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="syl-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">📚</div>
                <div>
                  <h2>{modalType === "add" ? "Create New Syllabus" : "Edit Syllabus"}</h2>
                  <span>{formData.Course} · {formData.Semester}</span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="modal-tabs">
              <button type="button" className={`modal-tab ${activeTab === "info" ? "active" : ""}`} onClick={() => setActiveTab("info")}>General Info</button>
              <button type="button" className={`modal-tab ${activeTab === "subjects" ? "active" : ""}`} onClick={() => setActiveTab("subjects")}>
                Subjects <span className="tab-badge">{formData.Subjects.length}</span>
              </button>
              <button type="button" className={`modal-tab ${activeTab === "resources" ? "active" : ""}`} onClick={() => setActiveTab("resources")}>Resources</button>
              <button type="button" className={`modal-tab ${activeTab === "status" ? "active" : ""}`} onClick={() => setActiveTab("status")}>Status</button>
            </div>
            <form onSubmit={handleSave}>
              {activeTab === "info" && (
                <div className="tab-content">
                  <div className="form-section-label">Course & Academic Year</div>
                  <div className="form-group full">
                    <label>Primary Course</label>
                    <select value={formData.Course || ""} onChange={e => setFormData({...formData, Course: e.target.value})}>
                      {COURSES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Semester</label>
                      <select value={formData.Semester || ""} onChange={e => setFormData({...formData, Semester: e.target.value})}>
                        {SEMESTERS.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Academic Year</label>
                      <select value={formData.Academic_Year || ""} onChange={e => setFormData({...formData, Academic_Year: e.target.value})}>
                        {ACADEMIC_YEARS.map(y => <option key={y} value={y}>{y}</option>)}
                      </select>
                    </div>
                  </div>
                  
                  <div className="form-section-label">Official Identification</div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Syllabus Code <span className="req">*</span></label>
                      <input 
                        type="text"
                        value={formData.Syllabus_Code || ""} 
                        onChange={e => setFormData({...formData, Syllabus_Code: e.target.value})} 
                        placeholder="e.g. SYL-CS-2024-V1"
                        required 
                      />
                    </div>
                    <div className="form-group">
                      <label>Approved By</label>
                      <input 
                        type="text"
                        value={formData.Approved_By || ""} 
                        onChange={e => setFormData({...formData, Approved_By: e.target.value})} 
                        placeholder="e.g. Dean of Academics"
                      />
                    </div>
                  </div>
                </div>
              )}
              {activeTab === "subjects" && (
                <div className="tab-content">
                  <div className="form-section-label">Subjects & Credits Breakdown</div>
                  <div className="subject-list">
                    {formData.Subjects.map((sub, idx) => (
                      <div key={sub.id} className="subject-item-card">
                        <div className="sic-header">
                          <span>Subject #{idx + 1}</span>
                          <button type="button" className="remove-sub" onClick={() => setFormData({...formData, Subjects: formData.Subjects.filter((_, i) => i !== idx)})}>Remove</button>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Subject Name</label>
                            <input 
                              type="text"
                              value={sub.Subject_Name || ""} 
                              onChange={e => { const s = [...formData.Subjects]; s[idx].Subject_Name = e.target.value; setFormData({...formData, Subjects: s}) }} 
                              placeholder="e.g. Operating Systems"
                            />
                          </div>
                          <div className="form-group">
                            <label>Subject Code</label>
                            <input 
                              type="text"
                              value={sub.Code || ""} 
                              onChange={e => { const s = [...formData.Subjects]; s[idx].Code = e.target.value; setFormData({...formData, Subjects: s}) }} 
                              placeholder="e.g. CS-401"
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Credits</label>
                            <input 
                              type="number" 
                              value={sub.Credits || ""} 
                              onChange={e => { const s = [...formData.Subjects]; s[idx].Credits = Number(e.target.value); setFormData({...formData, Subjects: s}) }} 
                            />
                          </div>
                          <div className="form-group">
                            <label>Units</label>
                            <input 
                              type="number" 
                              value={sub.Units || ""} 
                              onChange={e => { const s = [...formData.Subjects]; s[idx].Units = Number(e.target.value); setFormData({...formData, Subjects: s}) }} 
                            />
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Type</label>
                            <select value={sub.Type || "Core"} onChange={e => { const s = [...formData.Subjects]; s[idx].Type = e.target.value; setFormData({...formData, Subjects: s}) }}>
                              {SUBJECT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                            </select>
                          </div>
                          <div className="form-group">
                            <label>Exam Pattern</label>
                            <select value={sub.Exam_Pattern || "Theory"} onChange={e => { const s = [...formData.Subjects]; s[idx].Exam_Pattern = e.target.value; setFormData({...formData, Subjects: s}) }}>
                              {EXAM_PATTERNS.map(p => <option key={p} value={p}>{p}</option>)}
                            </select>
                          </div>
                        </div>
                        <div className="form-row">
                          <div className="form-group">
                            <label>Internal Marks</label>
                            <input 
                              type="number" 
                              value={sub.Internal_Marks || ""} 
                              onChange={e => { const s = [...formData.Subjects]; s[idx].Internal_Marks = Number(e.target.value); setFormData({...formData, Subjects: s}) }} 
                            />
                          </div>
                          <div className="form-group">
                            <label>External Marks</label>
                            <input 
                              type="number" 
                              value={sub.External_Marks || ""} 
                              onChange={e => { const s = [...formData.Subjects]; s[idx].External_Marks = Number(e.target.value); setFormData({...formData, Subjects: s}) }} 
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button type="button" className="add-sub-btn" onClick={() => setFormData({...formData, Subjects: [...formData.Subjects, mkSubject()]})}>
                    + Add New Subject
                  </button>
                </div>
              )}
              {activeTab === "resources" && (
                <div className="tab-content">
                  <div className="form-section-label">External Resources</div>
                  <div className="form-group full">
                    <label>Syllabus PDF URL</label>
                    <input 
                      type="text" 
                      value={formData.Syllabus_PDF_URL || ""} 
                      onChange={e => setFormData({...formData, Syllabus_PDF_URL: e.target.value})} 
                      placeholder="https://..."
                    />
                  </div>
                  <div className="form-group full">
                    <label>Reference Books / Bibliography</label>
                    <textarea 
                      rows="4"
                      value={formData.Reference_Books || ""} 
                      onChange={e => setFormData({...formData, Reference_Books: e.target.value})} 
                      placeholder="Title - Author; Title2 - Author2..."
                    />
                  </div>
                </div>
              )}
              {activeTab === "status" && (
                <div className="tab-content">
                  <div className="form-section-label">Review & Publishing</div>
                  <div className="form-group full">
                    <label>Lifecycle Status</label>
                    <select value={formData.Status || "Draft"} onChange={e => setFormData({...formData, Status: e.target.value})}>
                      {Object.keys(STATUS_META).map(k => <option key={k} value={k}>{k}</option>)}
                    </select>
                  </div>
                  <div className="form-group full">
                    <label>Internal Remarks</label>
                    <textarea 
                      rows="3"
                      value={formData.Internal_Remarks || ""} 
                      onChange={e => setFormData({...formData, Internal_Remarks: e.target.value})} 
                    />
                  </div>
                </div>
              )}
              <div className="modal-footer">
                <button type="button" className="btn btn-light" onClick={closeModal}>Cancel</button>
                <button type="submit" className="btn btn-primary">{modalType === "add" ? "Create Syllabus" : "Save Changes"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmId && (
        <div className="modal-overlay" onClick={() => setConfirmId(null)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <h3>{confirmAction === "delete" ? "Delete?" : "Publish?"}</h3>
            <div className="confirm-actions"><button onClick={() => setConfirmId(null)}>Cancel</button><button onClick={() => confirmAction === "delete" ? handleDelete(confirmId) : handleStatusChange(confirmId, "Active")}>Confirm</button></div>
          </div>
        </div>
      )}

      {/* ── View Modal ── */}
      {modalType === "view" && selectedItem && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="syl-modal view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <div className="modal-header-left">
                <div className="modal-header-icon">{COURSE_ICONS[selectedItem.Course] || "📘"}</div>
                <div>
                  <h2>{selectedItem.Course} Syllabus</h2>
                  <span>{selectedItem.Syllabus_Code} · {selectedItem.Semester}</span>
                </div>
              </div>
              <button type="button" className="modal-close" onClick={closeModal}>×</button>
            </div>
            
            <div className="view-content">
              <div className="view-hero">
                <div className="view-hero-main">
                  <div className="vh-item"><label>Academic Year</label><strong>{selectedItem.Academic_Year}</strong></div>
                  <div className="vh-item"><label>Status</label><span className="status-badge" style={{ color: STATUS_META[selectedItem.Status]?.color, background: STATUS_META[selectedItem.Status]?.bg }}>{selectedItem.Status}</span></div>
                  <div className="vh-item"><label>Approved By</label><strong>{selectedItem.Approved_By || "Pending"}</strong></div>
                </div>
                <button className="vh-edit-btn" onClick={() => openEditModal(selectedItem)}>Edit Syllabus</button>
              </div>

              <div className="view-section">
                <div className="view-section-label">Subjects & Credits</div>
                <div className="view-table-wrapper">
                  <table className="view-subjects-table">
                    <thead>
                      <tr>
                        <th>Code</th>
                        <th>Subject Name</th>
                        <th>Type</th>
                        <th>Exam</th>
                        <th>Credits</th>
                        <th>Units</th>
                        <th>Marks (I/E)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem.Subjects?.map((sub, i) => (
                        <tr key={i}>
                          <td><code>{sub.Code}</code></td>
                          <td><strong>{sub.Subject_Name}</strong></td>
                          <td><span className={`type-tag ${sub.Type?.toLowerCase()}`}>{sub.Type}</span></td>
                          <td>{sub.Exam_Pattern}</td>
                          <td>{sub.Credits}</td>
                          <td>{sub.Units}</td>
                          <td>{sub.Internal_Marks}/{sub.External_Marks}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4">Total Summary</td>
                        <td>{selectedItem.Subjects?.reduce((a, s) => a + (Number(s.Credits) || 0), 0)}</td>
                        <td>{selectedItem.Subjects?.reduce((a, s) => a + (Number(s.Units) || 0), 0)}</td>
                        <td>—</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>

              <div className="view-grid-two">
                <div className="view-section">
                  <div className="view-section-label">Resources</div>
                  <div className="resource-links">
                    {selectedItem.Syllabus_PDF_URL ? (
                      <a href={selectedItem.Syllabus_PDF_URL} target="_blank" rel="noreferrer" className="res-link pdf">📄 View Syllabus PDF</a>
                    ) : <span className="res-none">No PDF linked</span>}
                    <div className="ref-books">
                      <label>Reference Books:</label>
                      <p>{selectedItem.Reference_Books || "No references listed."}</p>
                    </div>
                  </div>
                </div>
                <div className="view-section">
                  <div className="view-section-label">Internal Remarks</div>
                  <div className="remarks-box">
                    {selectedItem.Internal_Remarks || "No internal remarks."}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Syllabus;